import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { followService, UserSuggestion, UserSearchResult, FollowRequest } from '../../services';
import toast from 'react-hot-toast';

export interface FollowState {
  suggestions: UserSuggestion[];
  searchResults: UserSearchResult[];
  loading: boolean;
  error: string | null;
  followingUsers: string[];
  pendingRequests: string[];
}

const initialState: FollowState = {
  suggestions: [],
  searchResults: [],
  loading: false,
  error: null,
  followingUsers: [],
  pendingRequests: [],
};

export const followUser = createAsyncThunk(
  'follow/followUser',
  async (data: FollowRequest, { rejectWithValue }) => {
    try {
      const response = await followService.followUser(data);
      if (response.success) {
        toast.success(response.message || 'Follow request sent successfully');
        return data.recipientId;
      } else {
        toast.error(response.message || 'Failed to send follow request');
        return rejectWithValue(response.message || 'Failed to send follow request');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send follow request';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const unfollowUser = createAsyncThunk(
  'follow/unfollowUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await followService.unfollowUser(userId);
      if (response.success) {
        toast.success(response.message || 'User unfollowed successfully');
        return userId;
      } else {
        toast.error(response.message || 'Failed to unfollow user');
        return rejectWithValue(response.message || 'Failed to unfollow user');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to unfollow user';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const cancelFollowRequest = createAsyncThunk(
  'follow/cancelFollowRequest',
  async (data: FollowRequest, { rejectWithValue }) => {
    try {
      const response = await followService.cancelFollowRequest(data);
      if (response.success) {
        toast.success(response.message || 'Follow request canceled successfully');
        return data.recipientId;
      } else {
        toast.error(response.message || 'Failed to cancel follow request');
        return rejectWithValue(response.message || 'Failed to cancel follow request');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to cancel follow request';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const followSlice = createSlice({
  name: 'follow',
  initialState,
  reducers: {
    setFollowingStatus: (state, action: PayloadAction<{ userId: string; isFollowing: boolean }>) => {
      const { userId, isFollowing } = action.payload;
      if (isFollowing) {
        if (!state.followingUsers.includes(userId)) {
          state.followingUsers.push(userId);
        }
        state.pendingRequests = state.pendingRequests.filter(id => id !== userId);
      } else {
        state.followingUsers = state.followingUsers.filter(id => id !== userId);
      }
      
      // Update search results
      state.searchResults = state.searchResults.map(user => 
        user._id === userId 
          ? { ...user, isFollowing, hasRequestPending: false }
          : user
      );
    },
    setPendingStatus: (state, action: PayloadAction<{ userId: string; isPending: boolean }>) => {
      const { userId, isPending } = action.payload;
      if (isPending) {
        if (!state.pendingRequests.includes(userId)) {
          state.pendingRequests.push(userId);
        }
      } else {
        state.pendingRequests = state.pendingRequests.filter(id => id !== userId);
      }
      
      // Update search results
      state.searchResults = state.searchResults.map(user => 
        user._id === userId 
          ? { ...user, hasRequestPending: isPending }
          : user
      );
    },
    setSuggestions: (state, action: PayloadAction<UserSuggestion[]>) => {
      state.suggestions = action.payload;
    },
    setSearchResults: (state, action: PayloadAction<UserSearchResult[]>) => {
      state.searchResults = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(followUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(followUser.fulfilled, (state, action) => {
        state.loading = false;
        const userId = action.payload;
        if (!state.pendingRequests.includes(userId)) {
          state.pendingRequests.push(userId);
        }
        
        // Update search results to show pending status
        state.searchResults = state.searchResults.map(user => 
          user._id === userId 
            ? { ...user, hasRequestPending: true }
            : user
        );
      })
      .addCase(followUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(unfollowUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unfollowUser.fulfilled, (state, action) => {
        state.loading = false;
        const userId = action.payload;
        state.followingUsers = state.followingUsers.filter(id => id !== userId);
        state.pendingRequests = state.pendingRequests.filter(id => id !== userId);
        
        // Update search results to show unfollowed status
        state.searchResults = state.searchResults.map(user => 
          user._id === userId 
            ? { ...user, isFollowing: false, hasRequestPending: false }
            : user
        );
      })
      .addCase(unfollowUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cancelFollowRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelFollowRequest.fulfilled, (state, action) => {
        state.loading = false;
        const userId = action.payload;
        state.pendingRequests = state.pendingRequests.filter(id => id !== userId);

        // Update search results to show canceled status
        state.searchResults = state.searchResults.map(user =>
          user._id === userId
            ? { ...user, hasRequestPending: false }
            : user
        );

        // Update suggestions to show canceled status
        state.suggestions = state.suggestions.map(user =>
          user._id === userId
            ? { ...user, hasRequestPending: false }
            : user
        );
      })
      .addCase(cancelFollowRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFollowingStatus,
  setPendingStatus,
  setSuggestions,
  setSearchResults,
  clearError,
} = followSlice.actions;

export default followSlice.reducer;